import './css/index.pcss';
import './css/font.pcss';
import './css/menus.pcss';
import './css/editors.pcss';
import './css/drag.pcss';
import './css/active-todo.pcss';
import './css/planner.pcss';
import './css/calendar.pcss';
import './css/library.pcss';
import './css/print.pcss';
import './css/flash-cards.pcss';
import './css/openu.pcss';
import './css/food.pcss';
import 'toastify-js/src/toastify.css';
import React, { lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import Login from './membership/ui/login';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import PlannerHome from './projects/planner/planner-home';
import Preloader from './common/ui/preloader';
import DiagramHome from './projects/diagram/ui/diagram-home';
import OverviewHomeOld from './projects/overview-old/overview-home';
import TagsView from './projects/planner/ui/tags/tags-view';
import DescartesSquareHome from './projects/descartes-square/descartes-square-home';
import BlocksOverviewHome from './projects/blocks/blocks-overview-home';
import FlashcardsHome from './projects/flashcards/flashcards-home';
import StudyView from './projects/flashcards/ui/study/study-view';
import LibraryHome from './projects/library/ui/library-home';
import PrintTodo from './projects/print/print-todo';
import LibraryReadView from './projects/library/ui/read/read-view';
import LibraryTagsView from './projects/library/ui/tags/tags-view/tags-view';
import OverviewHome from './projects/overview/overview-home';
import FinanceHome from './projects/finance/ui/finance-home';
import FoodHome from './projects/food/ui/food-home';
import DrawIO from './projects/drawio/diagrams-home';
import HabitTrackerHome from './projects/habit-tracker/habit-tracker-home';
import TodoTitleChartHome from './projects/todo-title-chart/todo-title-chart-home';
import FlowersHome from './projects/flowers/ui/flowers-home';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { getAllProjects, MENU_PROJECTS_QUERY_KEY } from './projects/planner/data/planner-data-service';
import OpenuSolverHome from './projects/openu-solver/openu-solver-home';
import YearBlocksIndex from './projects/blocks-year/blocks-year-index';
import InteriorOrganizerHome from './projects/interior-organizer/interior-organizer-home';
import MainLayout from './layouts/main-layout';
import ProjectMenu from './projects/planner/ui/projects/project-menu';
import WikiMenu from './projects/wiki/ui/menu/wiki-menu';
import FlashCardsMenu from './projects/flashcards/ui/menu/flashcards-menu';
import GalleryMenu from './projects/gallery/ui/menu/gallery-menu';
import LibraryMenu from './projects/library/ui/menu/library-menu';
import FinanceMenu from './projects/finance/ui/finance-menu';
import FoodMenu from './projects/food/ui/food-menu';
import FlowersMenu from './projects/flowers/ui/flowers-menu';
import AdminMenu from './projects/admin/ui/menu/admin-menu';
import DiagramsMenu from './projects/drawio/ui/menu/diagrams-menu';
import InteriorOrganizerMenu from './projects/interior-organizer/ui/interior-menu';
import OpenuSolverMenu from './projects/openu-solver/ui/menu/openu-solver-menu';

const WikiHome = lazy(() => import('./projects/wiki/ui/wiki-home'));
const CalendarHome = lazy(() => import('./projects/calendar/calendar-home'));
const AdminHome = lazy(() => import('./projects/admin/admin-home'));
const SearchView = lazy(() => import('./projects/planner/ui/search/search-view'));
const GalleryHome = lazy(() => import('./projects/gallery/ui/gallery-home'));
const FileSystemViewer = lazy(() => import('./projects/admin/ui/file-system-viewer/file-system-viewer'));
const UsersHome = lazy(() => import('./projects/admin/ui/users/users-home'));

const init = () => {
    const $root = document.getElementById('app');
    if(!$root) return;

    if(document.location.hostname === 'localhost') {
        document.title += ' - DEV';
    }

    const root = createRoot($root);

    const queryClient = new QueryClient();

    const plannerLoader = () => {
        return queryClient.fetchQuery({
            queryKey: [MENU_PROJECTS_QUERY_KEY],
            queryFn: getAllProjects,
        })
    };

    const router = createBrowserRouter([
        {
            path: '/',
            element: <Suspense fallback={<Preloader />}>
                <Login />
            </Suspense>,
        },
        {
            path: '/planner',
            element: <MainLayout additionalMenu={ <ProjectMenu /> } />,
            children: [
                {
                    index: true,
                    element: <Suspense fallback={<Preloader />}>
                        <PlannerHome />
                    </Suspense>,
                    loader: plannerLoader,
                },
                {
                    path: '/planner/:project_id',
                    element: <Suspense fallback={<Preloader />}>
                        <QueryClientProvider client={ queryClient }>
                            <PlannerHome />
                        </QueryClientProvider>
                    </Suspense>,
                    loader: plannerLoader,
                },
                {
                    path: '/planner/search',
                    element: <Suspense fallback={<Preloader />}>
                        <SearchView />
                    </Suspense>,
                    loader: plannerLoader,
                },
                {
                    path: '/planner/search/:search_term',
                    element: <Suspense fallback={<Preloader />}>
                        <SearchView />
                    </Suspense>,
                },
                {
                    path: '/planner/tags',
                    element: <Suspense fallback={<Preloader />}>
                        <TagsView />
                    </Suspense>,
                    loader: plannerLoader,
                },
            ]
        },
        {
            path: '/planner/calendar',
            element: <MainLayout
                hideSideBarToggle={ true }
                showMenuIcons={ true }
            />,
            children: [
                {
                    index: true,
                    path: '/planner/calendar',
                    element: <Suspense fallback={<Preloader />}>
                        <CalendarHome />
                    </Suspense>,
                },
            ]
        },
        {
            path: '/diagram/:todo_id',
            element: <Suspense fallback={<Preloader />}>
                <DiagramHome />
            </Suspense>,
        },
        {
            path: '/descartes-square/:todo_id',
            element: <MainLayout
                hideSideBarToggle={ true }
                showMenuIcons={ true }
            />,
            children: [
                {
                    index: true,
                    element: <Suspense fallback={<Preloader />}>
                        <DescartesSquareHome />
                    </Suspense>,
                },
            ]
        },
        {
            path: '/habit-tracker/:todo_id',
            element: <MainLayout
                hideSideBarToggle={ true }
                showMenuIcons={ true }
            />,
            children: [
                {
                    index: true,
                    element: <Suspense fallback={<Preloader />}>
                        <HabitTrackerHome />
                    </Suspense>,
                },
            ]
        },
        {
            path: '/todo-title-chart/:todo_id',
            element: <MainLayout
                hideSideBarToggle={ true }
                showMenuIcons={ true }
            />,
            children: [
                {
                    index: true,
                    element: <Suspense fallback={<Preloader />}>
                        <TodoTitleChartHome />
                    </Suspense>,
                },
            ]
        },
        {
            path: '/wiki',
            element: <MainLayout additionalMenu={ <WikiMenu /> } />,
            children: [
                {
                    index: true,
                    element: <Suspense fallback={<Preloader />}>
                        <WikiHome />
                    </Suspense>,
                },
                {
                    path: '/wiki/search',
                    element: <Suspense fallback={<Preloader />}>
                        <WikiHome />
                    </Suspense>,
                },
                {
                    path: '/wiki/:file_or_folder_path',
                    element: <Suspense fallback={<Preloader />}>
                        <WikiHome />
                    </Suspense>,
                },
                {
                    path: '/wiki/:file_or_folder_path/*',
                    element: <Suspense fallback={<Preloader />}>
                        <WikiHome />
                    </Suspense>,
                },
            ]
        },
        {
            path: '/overview-old',
            element: <MainLayout
                hideSideBarToggle={ true }
                showMenuIcons={ true }
            />,
            children: [
                {
                    index: true,
                    element: <Suspense fallback={<Preloader />}>
                        <OverviewHomeOld />
                    </Suspense>,
                },
            ]
        },
        {
            path: '/overview',
            element: <MainLayout
                hideSideBarToggle={ true }
                showMenuIcons={ true }
            />,
            children: [
                {
                    index: true,
                    element: <Suspense fallback={<Preloader />}>
                        <OverviewHome />
                    </Suspense>,
                },
            ]
        },
        {
            path: '/blocks-year',
            element: <MainLayout
                hideSideBarToggle={ true }
                showMenuIcons={ true }
            />,
            children: [
                {
                    index: true,
                    element: <Suspense fallback={<Preloader />}>
                        <YearBlocksIndex />
                    </Suspense>,
                },
            ]
        },
        {
            path: '/blocks',
            element: <MainLayout
                hideSideBarToggle={ true }
                showMenuIcons={ true }
            />,
            children: [
                {
                    index: true,
                    element: <Suspense fallback={<Preloader />}>
                        <BlocksOverviewHome />
                    </Suspense>,
                },
            ]
        },
        {
            path: '/flashcards',
            element: <MainLayout additionalMenu={ <FlashCardsMenu /> } />,
            children: [
                {
                    index: true,
                    element: <Suspense fallback={<Preloader />}>
                        <FlashcardsHome />
                    </Suspense>,
                },
                {
                    path: '/flashcards/:selected_item_id',
                    element: <Suspense fallback={<Preloader />}>
                        <FlashcardsHome />
                    </Suspense>,
                },
                {
                    path: '/flashcards/study/:deck_id',
                    element: <Suspense fallback={<Preloader />}>
                        <StudyView />
                    </Suspense>,
                },
                {
                    path: '/flashcards/search',
                    element: <Suspense fallback={<Preloader />}>
                        <FlashcardsHome />
                    </Suspense>,
                },
            ]
        },
        {
            path: '/gallery',
            element: <MainLayout additionalMenu={ <GalleryMenu /> } />,
            children: [
                {
                    index: true,
                    element: <Suspense fallback={<Preloader />}>
                        <GalleryHome />
                    </Suspense>,
                },
                {
                    path: '/gallery/:album_id',
                    element: <Suspense fallback={<Preloader />}>
                        <GalleryHome />
                    </Suspense>,
                },
            ]
        },
        {
            path: '/library',
            element: <MainLayout additionalMenu={ <LibraryMenu /> } />,
            children: [
                {
                    index: true,
                    element: <Suspense fallback={<Preloader />}>
                        <LibraryHome />
                    </Suspense>,
                },
                {
                    path: '/library/:path',
                    element: <Suspense fallback={<Preloader />}>
                        <LibraryHome />
                    </Suspense>,
                },
                {
                    path: '/library/tags',
                    element: <Suspense fallback={<Preloader />}>
                        <LibraryTagsView />
                    </Suspense>,
                },
            ]
        },
        {
            path: '/library/read/:book_file_id',
            element: <MainLayout
                hideSideBarToggle={ true }
                showMenuIcons={ true }
            />,
            children: [
                {
                    index: true,
                    element: <Suspense fallback={<Preloader />}>
                        <LibraryReadView />
                    </Suspense>,
                },
            ]
        },
        {
            path: '/print/todo/:todo_id',
            element: <Suspense fallback={<Preloader />}>
                <PrintTodo />
            </Suspense>,
        },
        {
            path: '/finance',
            element: <MainLayout additionalMenu={ <FinanceMenu /> } />,
            children: [
                {
                    index: true,
                    element: <Suspense fallback={<Preloader />}>
                        <FinanceHome />
                    </Suspense>,
                },
                {
                    path: '/finance/categories',
                    element: <Suspense fallback={<Preloader />}>
                        <FinanceHome />
                    </Suspense>,
                },
                {
                    path: '/finance/businesses',
                    element: <Suspense fallback={<Preloader />}>
                        <FinanceHome />
                    </Suspense>,
                },
                {
                    path: '/finance/transactions',
                    element: <Suspense fallback={<Preloader />}>
                        <FinanceHome />
                    </Suspense>,
                },
                {
                    path: '/finance/gas',
                    element: <Suspense fallback={<Preloader />}>
                        <FinanceHome />
                    </Suspense>,
                },
            ]
        },
        {
            path: '/food',
            element: <MainLayout additionalMenu={ <FoodMenu /> } />,
            children: [
                {
                    index: true,
                    element: <Suspense fallback={<Preloader />}>
                        <FoodHome />
                    </Suspense>,
                },
                {
                    path: '/food/food-items',
                    element: <Suspense fallback={<Preloader />}>
                        <FoodHome />
                    </Suspense>,
                },
                {
                    path: '/food/categories',
                    element: <Suspense fallback={<Preloader />}>
                        <FoodHome />
                    </Suspense>,
                },
                {
                    path: '/food/calendar',
                    element: <Suspense fallback={<Preloader />}>
                        <FoodHome />
                    </Suspense>,
                },
                {
                    path: '/food/export',
                    element: <Suspense fallback={<Preloader />}>
                        <FoodHome />
                    </Suspense>,
                },

                {
                    path: '/food/goals',
                    element: <Suspense fallback={<Preloader />}>
                        <FoodHome />
                    </Suspense>,
                },
            ]
        },
        {
            path: '/flowers',
            element: <MainLayout additionalMenu={ <FlowersMenu /> } />,
            children: [
                {
                    index: true,
                    element: <Suspense fallback={<Preloader />}>
                        <FlowersHome />
                    </Suspense>,
                },
                {
                    path: '/flowers/plants',
                    element: <Suspense fallback={<Preloader />}>
                        <FlowersHome />
                    </Suspense>,
                },
                {
                    path: '/flowers/types',
                    element: <Suspense fallback={<Preloader />}>
                        <FlowersHome />
                    </Suspense>,
                },
                {
                    path: '/flowers/locations',
                    element: <Suspense fallback={<Preloader />}>
                        <FlowersHome />
                    </Suspense>,
                },
                {
                    path: '/flowers/plant-calendar/:plant_id',
                    element: <Suspense fallback={<Preloader />}>
                        <FlowersHome />
                    </Suspense>,
                },
                {
                    path: '/flowers/gallery',
                    element: <Suspense fallback={<Preloader />}>
                        <FlowersHome />
                    </Suspense>,
                },
            ]
        },
        {
            path: '/admin',
            element: <MainLayout additionalMenu={ <AdminMenu /> } />,
            children: [
                {
                    index: true,
                    element: <Suspense fallback={<Preloader />}>
                        <AdminHome />
                    </Suspense>,
                },
                {
                    path: '/admin/file-system-viewer',
                    element: <Suspense fallback={<Preloader />}>
                        <FileSystemViewer />
                    </Suspense>,
                },
                {
                    path: '/admin/users',
                    element: <Suspense fallback={<Preloader />}>
                        <UsersHome />
                    </Suspense>,
                },
            ]
        },
        {
            path: '/draw-io',
            element: <MainLayout additionalMenu={ <DiagramsMenu /> } />,
            children: [
                {
                    index: true,
                    element: <Suspense fallback={<Preloader />}>
                        <DrawIO />
                    </Suspense>,
                },
                {
                    path: '/draw-io/:selected_item_id',
                    element: <Suspense fallback={<Preloader />}>
                        <DrawIO />
                    </Suspense>,
                },
            ]
        },
        {
            path: '/openu-solver/*',
            element: <MainLayout additionalMenu={ <OpenuSolverMenu /> } />,
            children: [
                {
                    path: '*',
                    element: <Suspense fallback={<Preloader />}>
                        <OpenuSolverHome />
                    </Suspense>,
                },
            ]
        },

        {
            path: '/interior-organizer',
            element: <MainLayout additionalMenu={ <InteriorOrganizerMenu /> } />,
            children: [
                {
                    index: true,
                    element: <Suspense fallback={<Preloader />}>
                        <InteriorOrganizerHome />
                    </Suspense>,
                },
                {
                    path: '/interior-organizer/storage-item-categories',
                    element: <Suspense fallback={<Preloader />}>
                        <InteriorOrganizerHome />
                    </Suspense>,
                },
                {
                    path: '/interior-organizer/storage-items',
                    element: <Suspense fallback={<Preloader />}>
                        <InteriorOrganizerHome />
                    </Suspense>,
                },
            ]
        },
    ]);

    root.render(
        <React.StrictMode>
            <QueryClientProvider client={ queryClient }>
                <RouterProvider router={router} />
            </QueryClientProvider>
        </React.StrictMode>
    );
};

document.addEventListener('DOMContentLoaded', () => {
    init();
});